import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

/*
  Manages file drag-and-drop and paperclip-triggered upload for conversations.
  Drag/drop is scoped to this controller's element (the assistant panel/frame),
  so it doesn't collide with other dropzones on the page (e.g. the Writer's
  import area when the assistant runs as a sidebar). Shows an overlay during
  drag and uploads the dropped/selected file straight to the conversation
  documents endpoint via Turbo Stream (the processing card in the conversation
  provides upload feedback). Documents are always scoped to the current
  conversation.

  Values:
    - upload-url (String): POST endpoint for conversation_documents#create
    - persisted (Boolean): Guards the controller — no-op when false (unsaved conversation)

  Targets:
    - overlay: Full-page drag-over indicator (fixed, covers viewport)
    - fileInput: Hidden <input type="file"> triggered by the paperclip button

  Example:
    <div data-controller="conversation-dropzone"
         data-conversation-dropzone-upload-url-value="/asistente/abc/conversation_documents"
         data-conversation-dropzone-persisted-value="true">

      <input type="file"
             data-conversation-dropzone-target="fileInput"
             data-action="change->conversation-dropzone#onFileSelected"
             accept=".pdf,.txt,.md,.doc,.docx"
             class="hidden">

      <div data-conversation-dropzone-target="overlay" class="hidden fixed inset-0 z-50 ..."></div>

      <button data-action="click->conversation-dropzone#openFilePicker">📎</button>
    </div>
*/

const ACCEPTED_MIME_TYPES = new Set([
  "application/pdf",
  "text/plain",
  "text/markdown",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);

const ACCEPTED_EXTENSIONS = new Set([".pdf", ".txt", ".md", ".doc", ".docx"]);

export default class extends Controller {
  static targets = ["overlay", "fileInput", "uploadUrl"];
  static values = {
    uploadUrl: String,
    persisted: { type: Boolean, default: false },
  };

  connect() {
    if (!this.persistedValue) return;

    this._dragCounter = 0;

    this._boundDragEnter = this._onDragEnter.bind(this);
    this._boundDragOver = this._onDragOver.bind(this);
    this._boundDragLeave = this._onDragLeave.bind(this);
    this._boundDrop = this._onDrop.bind(this);

    // Scope drag/drop to this controller's element (the assistant panel/frame),
    // not the whole document. Otherwise, when the assistant runs as a sidebar
    // next to another dropzone (e.g. the Writer's import area), a single drop
    // fires both this dropzone and the other one.
    this.element.addEventListener("dragenter", this._boundDragEnter);
    this.element.addEventListener("dragover", this._boundDragOver);
    this.element.addEventListener("dragleave", this._boundDragLeave);
    this.element.addEventListener("drop", this._boundDrop);
  }

  disconnect() {
    this.element.removeEventListener("dragenter", this._boundDragEnter);
    this.element.removeEventListener("dragover", this._boundDragOver);
    this.element.removeEventListener("dragleave", this._boundDragLeave);
    this.element.removeEventListener("drop", this._boundDrop);
  }

  openFilePicker() {
    if (!this.persistedValue) return;
    this.fileInputTarget.click();
  }

  onFileSelected(event) {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file) return;
    this._handleFile(file);
  }

  // Private

  _onDragEnter(event) {
    if (!this._hasFiles(event)) return;
    event.preventDefault();
    this._dragCounter++;
    if (this._dragCounter === 1) this._showOverlay();
  }

  _onDragOver(event) {
    if (!this._hasFiles(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  }

  _onDragLeave(_event) {
    if (this._dragCounter === 0) return;
    this._dragCounter--;
    if (this._dragCounter === 0) this._hideOverlay();
  }

  _onDrop(event) {
    this._dragCounter = 0;
    this._hideOverlay();
    if (!this._hasFiles(event)) return;
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) this._handleFile(file);
  }

  _handleFile(file) {
    if (!this._isValidFile(file)) {
      this._flashError(I18n.conversation_dropzone.invalid_file_type);
      return;
    }
    this._upload(file);
  }

  _hasFiles(event) {
    return event.dataTransfer?.types?.includes("Files");
  }

  _isValidFile(file) {
    if (ACCEPTED_MIME_TYPES.has(file.type)) return true;
    const ext = "." + file.name.split(".").pop().toLowerCase();
    return ACCEPTED_EXTENSIONS.has(ext);
  }

  _showOverlay() {
    this.overlayTarget.classList.remove("hidden");
  }

  _hideOverlay() {
    this.overlayTarget.classList.add("hidden");
  }

  _flashError(message) {
    const streamHtml = `<turbo-stream action="append" target="turbo-flash-container"><template><div class="flash-message-absolute-container" data-controller="autohide" data-autohide-target="container" data-autohide-animation-value="animate-fade-out-bottom"><div class="flash-message alert">${message}</div></div></template></turbo-stream>`;
    Turbo.renderStreamMessage(streamHtml);
  }

  // Read the URL from the frame-content holder when present: it re-renders on
  // every conversation switch, while uploadUrlValue (on the persistent frame) is
  // frozen at the first-loaded conversation. Fall back to the value otherwise.
  get _uploadUrl() {
    if (this.hasUploadUrlTarget) return this.uploadUrlTarget.dataset.url;
    return this.uploadUrlValue;
  }

  async _upload(file) {
    if (!file) return;

    const formData = new FormData();
    formData.append("conversation_document[file]", file);

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    try {
      const response = await fetch(this._uploadUrl, {
        method: "POST",
        headers: {
          Accept: "text/vnd.turbo-stream.html, text/html, application/xhtml+xml",
          "X-CSRF-Token": csrfToken,
        },
        body: formData,
      });
      const html = await response.text();
      Turbo.renderStreamMessage(html);
    } catch {
      this._flashError(I18n.conversation_dropzone.upload_error);
    }
  }
}
